<template>
  <v-card>
    <v-card-title>
      {{ $t(`variation.import.title.${type}`) }}
      <v-spacer/>
      <v-btn
        icon
        small
        @click="handleClose"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-subtitle>
      <div v-html="$t('variation.import.guide')" />
      <v-btn
        @click="handleGetTemplate"
        x-small
        outlined
        rounded
        color="primary"
      >
        {{ $t('variation.import.templateButton') }}
      </v-btn>
    </v-card-subtitle>
    <v-card-text>
      <v-file-input
        outlined
        dense
        :label="$t('variation.import.file')"
        v-model="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        @change="handleFileChange"
      />
      <ErrorsListAlert :items="errors" />
      <v-alert
        v-if="isAnyOrderSelected"
        type="info"
        outlined
        dense
      >
        {{ $t('variation.import.orderInfoMessage') }}
      </v-alert>
      <v-data-table
        v-model="selectedVariations"
        show-select
        :headers="headers"
        :items="variationsWithErrors"
        item-key="index"
        :item-class="item => item.errors.length ? 'error-row' : ''"
        :single-expand="false"
        show-expand
      >
        <template v-slot:header.data-table-select="{}">
          <v-simple-checkbox
            :value="selectAll"
            :ripple="false"
            @input="handleSelectAll"
          />
        </template>
        <template v-slot:item.data-table-select="{ item, isSelected, select }">
          <v-simple-checkbox
            :value="isSelected"
            @input="select"
            :ripple="false"
            :disabled="!!item.errors.length"
          />
        </template>
        <template #[`item.no`]="{ item }">
          <v-chip small :class="colorForStage[item.stage]">
            {{item.no}}
          </v-chip>
        </template>
        <template #[`item.lastUpdate`]="{ item }">
          <span class="nowrap">{{item.receivedDate | formatDate}}</span>
        </template>
        <template #[`item.eot`]="{ item }">
          <div class="d-flex flex-column">
            <span v-if="item.eotRequested !== null">
              <v-icon
                class="primary--text"
                small
              >mdi-calendar-clock</v-icon> {{item.eotRequested }}
            </span>
            <span v-if="item.eotAgreed !== null">
              <v-icon
                class="success--text"
                small
              >mdi-timetable</v-icon> {{item.eotAgreed }}
            </span>

          </div>
        </template>
        <template #[`item.value`]="{ item }">
          <div class="d-flex flex-column">
            <small>
              <span class="nowrap" v-if="item.estimatedValueCents.sum !== null">
                <v-icon
                  class="orange--text"
                  small
                >mdi-approximately-equal</v-icon> {{item.estimatedValueCents.sum | formatCurrency(chosenCurrency) }}
              </span>
            </small>
            <small>
              <span class="nowrap" v-if="item.originalValueCents.sum !== null">
                <v-icon
                  class="primary--text"
                  small
                >mdi-tag</v-icon> {{item.originalValueCents.sum | formatCurrency(chosenCurrency) }}
              </span>
            </small>
            <small class="">
              <span class="nowrap" v-if="item.agreedValueCents.sum !== null">
                <v-icon
                  class="success--text"
                  small
                >mdi-handshake</v-icon> {{item.agreedValueCents.sum |formatCurrency(chosenCurrency) }}
              </span>
            </small>
          </div>
        </template>
        <template #[`item.category`]="{ item }">
          <div v-if="item.category" class="category-chip">
            <v-chip
              v-if="item.category"
              class="yellow text-center"
              small
            >
              {{item.category}}
            </v-chip>
          </div>
        </template>
        <template #[`item.workstream`]="{ item }">
          <div v-if="item.category" class="category-chip">
            <v-chip
              v-if="item.workstream"
              class="cyan lighten-3 text-center"
              small
            >
              {{item.workstream}}
            </v-chip>
          </div>
        </template>
        <template #[`item.status`]="{ item }">
          <div class="">
            <v-chip
              :color="statusesMap[item.status] ? statusesMap[item.status].color : ''"
              small
            >{{statusesMap[item.status] ? statusesMap[item.status].name : ''}}</v-chip>
          </div>
        </template>
        <template #[`item.risk`]="{ item }">
          <div class="text--primary">{{ item.risk.name }}</div>
          <div class="text--secondary">{{ item.risk.description }}</div>
        </template>

      <template #[`expanded-item`]="{ headers, item }">
        <td
          class="white py-2 "
          :colspan="headers.length"
        >
          <v-row>
            <v-col cols="12">
              <VariationsPartsTable
                :parts="item.parts"
                :reporter="item.reportingSide"
                :isMultiCurrency="contract.isMultiCurrency"
              />
            </v-col>
          </v-row>
        </td>
      </template>
      </v-data-table>
    </v-card-text>
    <v-row class="my-1 mx-2">
      <v-spacer />
      <v-col cols="12" md="6" lg="5" xl="4">
        <v-alert
          v-if="selectedVariations.length > 10"
          type="info"
          outlined
          dense
        >
          {{ $t('variation.import.longOperationWarning', { no: 10 }) }}
        </v-alert>
      </v-col>
    </v-row>
    <v-card-actions class="ma-1">
      <v-spacer />
      <v-btn
        rounded
        small
        :color="errors.length ? 'warning' : 'primary'"
        :disabled="!selectedVariations.length"
        @click="handleImport"
      >
        {{ $t('variation.import.button') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import XLSX from 'xlsx'
import { DEFAULT_CONTRACT, DEFAULT_VARIATION, ISO_DATE_FORMAT, VARIATIONS_HEADERS, getSumOfPrices } from '@/helpers'
import dayjs from 'dayjs'
import { generateVariationTemplate, getExcelLabels } from '@/helpers/variationsImport'
import { mapState } from 'vuex'
import { importVariationsForContract } from '../../services'
import { importVariationModel, errorMessageGenerator } from '@/validators'

export default {
  name: 'VariationsImport',
  props: {
    contract: {
      type: Object,
      default: () => DEFAULT_CONTRACT
    },
    variations: {
      type: Array,
      default: () => ([])
    },
    type: {
      type: String,
      default: 'variation'
    },
    chosenCurrency: Object
  },
  data () {
    const headers = VARIATIONS_HEADERS[this.type].map(i => {
      return {
        ...i,
        text: i.key ? this.$t(`variation.headers.${i.key}`) : ''
      }
    }).filter(i => i.value !== 'data-table-expand')
    headers.push({ value: 'risk', text: this.$tc('dictionaries.risk', 1) })
    headers.push({ value: 'description', text: this.$t('common.description') })
    this.type === 'variation' && this.contract.area.code === 'ON' &&
      headers.push({ value: 'workstream', text: this.$t('variation.workstream') })
    this.type === 'claim' && this.contract.area.code === 'ON' &&
      headers.push({ value: 'workstream', text: this.$t('variation.workstream') })
    this.type === 'claim' && headers.push({ value: 'reportingSide', text: this.$t('variation.headers.reportedBy') })
    headers.push({ value: 'data-table-expand' })
    return {
      headers,
      file: null,
      colorForStage: ['', 'orange', 'primary'],
      importedVariations: [],
      selectedVariations: [],
      excelLabels: getExcelLabels(this.type, this.contract.area)
    }
  },
  computed: {
    ...mapState({
      statusesMap: (state) => state.statuses.reduce((acc, curr) => ({ ...acc, [curr.code]: curr }), {}),
      risks: state => state.risks,
      variationsStages: (state) => state.variationsStages,
      statuses: state => state.statuses
    }),
    statusesList () {
      return this.statuses
        .filter((i) => i.show.includes(this.type))
        .map((i) => ({
          text: i.name,
          value: i.code,
          disabled: i.disabled.includes(this.type)
        }))
    },
    isAnyOrderSelected () {
      return this.type === 'variation' && this.selectedVariations.find(i => i.stage === 2)
    },
    selectAll () {
      return !!(this.selectedVariations.length &&
        this.selectedVariations.length === this.variationsWithErrors.filter(i => !i.errors.length).length)
    },
    stagesList () {
      return this.variationsStages[this.type].map((s, i) => ({
        name: s.name,
        value: i,
        prefix: s.prefix
      }))
    },
    errors () {
      return this.variationsWithErrors.map(i => i.errors).flat()
    },
    variationsWithErrors () {
      return this.importedVariations.map(i => {
        const errors = [
          this.checkIfNumberIsCorrect(i),
          this.checkIfStageIsCorrect(i),
          this.checkIfNumberWasUsed(i),
          this.checkIfNumberIsDuplicated(i),
          ...this.checkValidationErrors(i)
        ].filter(i => !!i)
        return { ...i, errors }
      })
    }
  },
  methods: {
    checkValidationErrors (item) {
      return Object.keys(item).map(key => {
        return errorMessageGenerator(this.$v.importedVariations.$each[item.index][key], key)
      }).flat().map(i => `${item.name || item.no} - ${i}`)
    },
    checkIfStageIsCorrect (item) {
      return (item.stage > -1 && (isNaN(item.number) || item.stage < 0))
        ? this.$t('variation.import.errors.stage', { name: item.name, no: item.no, stage: this.variationsStages[item.stage] })
        : null
    },
    checkIfNumberIsCorrect (item) {
      return (item.number === -1)
        ? this.$t('variation.import.errors.numberCorrect', { no: item.no })
        : null
    },
    checkIfNumberWasUsed (item) {
      const counters = { [this.type]: [], ...(this.contract.metadata.counters || {}) }
      if (item.no && counters[this.type][item.stage] >= item.number) {
        return this.$t('variation.import.errors.numberUsed', { no: item.no })
      }
      const prefixes = new RegExp(this.stagesList.map(i => i.prefix).join('|'), 'g')
      const checkNos = item.noList
        .map((no, index) => no && counters[this.type][index] >= Number(no.replace(prefixes, '')))
      const indexOfBadNo = checkNos.indexOf(true)
      if (indexOfBadNo > -1) {
        return this.$t('variation.import.errors.historyNumberUsed', { oldNo: item.noList[indexOfBadNo], currentNo: item.no })
      }
      return null
    },
    checkIfNumberIsDuplicated (item) {
      const noDuplicates = this.importedVariations.filter(i => i.no === item.no)
      if (noDuplicates.length > 1) {
        return this.$t('variation.import.errors.numberDuplicated', { no: item.no, name: item.name })
      }
      const noListDuplicates = item.noList.filter(number => number && this.importedVariations.filter(i => i.noList.includes(number)).length > 1)
      if (noListDuplicates.length) {
        return this.$t('variation.import.errors.historyNumberDuplicated', { no: noListDuplicates[0], name: item.name })
      }
      return null
    },
    handleGetTemplate () {
      generateVariationTemplate(this.type, this.contract.area)
    },
    handleParseFile (excel) {
      return XLSX.utils.sheet_to_json(excel.Sheets[this.type], { raw: false })
        .reduce((acc, curr) => {
          const excelItem = Object.entries(this.excelLabels).reduce((accItem, label) => ({ ...accItem, [label[0]]: curr[label[1]] }), {})
          const part = this.contract.parts.find(i => excelItem.partName && i.name.toUpperCase() === excelItem.partName.toUpperCase())
          const risk = this.risks.find(i => excelItem.riskName && i.name.toUpperCase() === excelItem.riskName.toUpperCase()) || {}
          const status = this.statusesList.find(i => excelItem.status && i.text.toUpperCase() === excelItem.status.toUpperCase())
          const noList = this.stagesList.map(stage => {
            const item = excelItem[stage.prefix]
            return item && !item.includes(stage.prefix) ? stage.prefix + item : item
          })
          const no = noList.filter(i => !!i)[noList.filter(i => !!i).length - 1]
          const stageBaseOnNo = noList.indexOf(no)
          const preparePart = (part) => {
            return {
              partId: part.id,
              name: part.name,
              ratio: part.ratio,
              currency: part.currency,
              estimatedValueCents: (Number(excelItem.estimatedValue) || 0) * 100,
              originalValueCents: (Number(excelItem.originalValue) || 0) * 100,
              agreedValueCents: (Number(excelItem.agreedValue) || 0) * 100,
              eotRequested: Number(excelItem.eotRequested) || 0,
              eotAgreed: Number(excelItem.eotAgreed) || 0
            }
          }
          if (acc[acc.length - 1] && part && noList.join('-') === acc[acc.length - 1].noList.join('-')) {
            const partIndex = acc[acc.length - 1].parts.findIndex(i => i.partId === part.id)
            partIndex === -1
              ? acc[acc.length - 1].parts.push(preparePart(part))
              : acc[acc.length - 1][partIndex] = preparePart(part)
          } else if (!acc[acc.length - 1] || (acc[acc.length - 1] && noList.join('-') !== acc[acc.length - 1].noList.join('-'))) {
            acc.push({
              ...DEFAULT_VARIATION[this.type],
              type: this.type,
              reportingSide: 'employer',
              ...excelItem,
              index: acc.length,
              no,
              noList,
              status: status ? status.value : null,
              stage: stageBaseOnNo,
              receivedDate: excelItem.receivedDate ? dayjs(excelItem.receivedDate, 'D/M/YY').format(ISO_DATE_FORMAT) : null,
              parts: excelItem.partName ? [preparePart(part)] : this.contract.parts.map(i => preparePart(i)),
              risk,
              number: stageBaseOnNo >= 0 && no ? Number(no.replace(this.variationsStages[this.type][stageBaseOnNo].prefix, '')) : -1
            })
          }
          acc[acc.length - 1].estimatedValueCents = getSumOfPrices(acc[acc.length - 1].parts, 'estimatedValueCents', this.contract.currencies)
          acc[acc.length - 1].originalValueCents = getSumOfPrices(acc[acc.length - 1].parts, 'originalValueCents', this.contract.currencies)
          acc[acc.length - 1].agreedValueCents = getSumOfPrices(acc[acc.length - 1].parts, 'agreedValueCents', this.contract.currencies)
          acc[acc.length - 1].eotRequested = acc[acc.length - 1].parts.reduce((acc, curr) => acc + curr.eotRequested, 0)
          acc[acc.length - 1].eotAgreed = acc[acc.length - 1].parts.reduce((acc, curr) => acc + curr.eotAgreed, 0)
          return acc
        }, [])
    },
    handleFileChange (v) {
      if (v) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const data = e.target.result
          const excel = XLSX.read(data, { type: 'binary' })
          this.importedVariations = this.handleParseFile(excel)
          this.$v.$touch()
        }
        reader.readAsBinaryString(v)
        reader.onerror = function (ex) { }
      }
    },
    async handleImport () {
      await importVariationsForContract(this.type, this.contract.id, this.selectedVariations)
      this.$emit('FETCH_DATA')
      this.handleClose()
    },
    handleClose () {
      this.$eventBus.$emit('CLOSE_MODAL')
      this.file = null
      this.importedVariations = []
    },
    handleSelectAll (value) {
      this.selectedVariations = value ? this.variationsWithErrors.filter(i => !i.errors.length) : []
    }
  },
  watch: { },
  validations: importVariationModel
}
</script>

<style>
.error-row {
  background-color:rgba(255, 49, 49, 0.2);
}
.warn-row {
  background-color:rgba(209, 126, 32, 0.2);
}
</style>
